/*
 * pcx-capture-form-field.js — form-field engagement / abandonment capture.
 *
 *   Halide-portable (spec replay-v2 R6): ZERO Shopify/Liquid dependencies.
 *   All wiring is injected via init(deps) by the adapter.
 *
 *   MASKING INVARIANT (spec replay-v2 E, identical to v1 replay posture):
 *   this module captures field NAMES only — NEVER field values. The only
 *   value-derived signal is a BOOLEAN (`blur_with_value`: did the field have
 *   any content when the user left it). No input string is ever read into a
 *   variable that can reach the payload. There is no code path that copies
 *   `el.value` into an event property — the boolean is computed from
 *   `el.value.length > 0` and the string is discarded immediately.
 *
 *   Events:
 *     form_field_engaged   — fired when a tracked form's field is focused;
 *                            properties: { form, field, blur_with_value? }
 *     form_abandoned       — fired on page-hide if a tracked form was engaged
 *                            but not submitted; properties: { form, fields:[names] }
 *
 *   Integration:
 *     PeterCoxCapture.formField.init({
 *       track: fn(eventType, properties),  // REQUIRED
 *       forms: ['newsletter','contact'],    // OPTIONAL — form ids to track (default: any with data-pcx-form)
 *       root: document                       // OPTIONAL — DOM root (for tests)
 *     });
 */
(function () {
  'use strict';

  /* PURE HELPERS BEGIN — extracted + unit-tested in tests/pcx-capture-form-field.test.mjs */

  // Resolve a tracked form's stable id from an element: prefer an explicit
  // data-pcx-form attribute, then the form's name/id. Returns null when the
  // element is not inside a tracked form (so we never capture from arbitrary
  // inputs).
  function formIdFor(el, getAttr) {
    if (!el) return null;
    var v = getAttr(el, 'data-pcx-form') || getAttr(el, 'name') || getAttr(el, 'id');
    return v || null;
  }

  // The field NAME for an input element — name attr, else id, else the input
  // type. NEVER the value. Returns null when no stable name is available.
  function fieldNameFor(el, getAttr) {
    if (!el) return null;
    var v = getAttr(el, 'name') || getAttr(el, 'id') || getAttr(el, 'type');
    return v || null;
  }

  // Compute the blur-with-value BOOLEAN from a value-length, not the value.
  // The caller passes el.value.length (a number) — the string itself never
  // crosses this boundary, so there is no way for a value to leak through.
  function blurHadValue(valueLength) {
    return typeof valueLength === 'number' && valueLength > 0;
  }

  // Build the form_field_engaged properties. blur_with_value is included only
  // when a blur boolean is supplied. NEVER carries a value string.
  function buildEngagedProperties(formId, fieldName, blurBool) {
    var props = { form: String(formId), field: String(fieldName) };
    if (typeof blurBool === 'boolean') props.blur_with_value = blurBool;
    return props;
  }

  // Build the form_abandoned properties from the set of engaged field NAMES.
  function buildAbandonedProperties(formId, fieldNames) {
    return { form: String(formId), fields: (fieldNames || []).map(String) };
  }

  /* PURE HELPERS END */

  function init(deps) {
    deps = deps || {};
    var track = deps.track;
    if (typeof track !== 'function') return false;
    var root = deps.root || (typeof document !== 'undefined' ? document : null);
    if (!root) return false;
    var allowForms = Array.isArray(deps.forms) ? deps.forms : null;

    function getAttr(el, name) {
      return (el && typeof el.getAttribute === 'function') ? el.getAttribute(name) : null;
    }
    function isTracked(formId) {
      if (!formId) return false;
      return allowForms ? allowForms.indexOf(formId) !== -1 : true;
    }

    // Per-form set of engaged field names + a submitted flag, for abandonment.
    var engaged = {};   // formId -> { fields: {name:true}, submitted: false }

    function ensure(formId) {
      if (!engaged[formId]) engaged[formId] = { fields: {}, submitted: false };
      return engaged[formId];
    }

    function onFocus(ev) {
      var el = ev.target;
      var form = el && el.form ? el.form : el;
      var formId = formIdFor(form, getAttr) || formIdFor(el, getAttr);
      if (!isTracked(formId)) return;
      var field = fieldNameFor(el, getAttr);
      if (!field) return;
      ensure(formId).fields[field] = true;
      track('form_field_engaged', buildEngagedProperties(formId, field));
    }

    function onBlur(ev) {
      var el = ev.target;
      var form = el && el.form ? el.form : el;
      var formId = formIdFor(form, getAttr) || formIdFor(el, getAttr);
      if (!isTracked(formId)) return;
      var field = fieldNameFor(el, getAttr);
      if (!field) return;
      // VALUE MASKING: read length only, never the string.
      var len = (el && typeof el.value === 'string') ? el.value.length : 0;
      track('form_field_engaged', buildEngagedProperties(formId, field, blurHadValue(len)));
    }

    function onSubmit(ev) {
      var form = ev.target;
      var formId = formIdFor(form, getAttr);
      if (isTracked(formId)) ensure(formId).submitted = true;
    }

    function flushAbandoned() {
      for (var formId in engaged) {
        if (!Object.prototype.hasOwnProperty.call(engaged, formId)) continue;
        var rec = engaged[formId];
        if (rec.submitted) continue;
        var names = Object.keys(rec.fields);
        if (names.length) track('form_abandoned', buildAbandonedProperties(formId, names));
      }
      engaged = {};
    }

    if (typeof root.addEventListener === 'function') {
      // Capture-phase focus/blur so they bubble from inputs.
      root.addEventListener('focus', onFocus, true);
      root.addEventListener('blur', onBlur, true);
      root.addEventListener('submit', onSubmit, true);
      var w = (typeof window !== 'undefined') ? window : null;
      if (w) {
        w.addEventListener('pagehide', flushAbandoned);
        w.addEventListener('visibilitychange', function () {
          if (w.document && w.document.visibilityState === 'hidden') flushAbandoned();
        });
      }
    }

    init._flushAbandoned = flushAbandoned;   // exposed for the adapter / tests
    return true;
  }

  window.PeterCoxCapture = window.PeterCoxCapture || {};
  window.PeterCoxCapture.formField = { init: init };
})();
